/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import {
  Component, HostListener, Inject, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { from, Observable } from 'rxjs';
import {
  groupBy, map, mergeMap, reduce, take,
} from 'rxjs/operators';
import {
  cloneDeep, differenceBy, uniqBy, uniqWith,
} from 'lodash-es';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { faAddressCardIcon } from 'IconLibrary/fontawesome/regular/address-card';
import { SvgIconRegistry } from '@ngneat/svg-icon';

import { MessageService } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { TranslocoService } from '@jsverse/transloco';
import { PageableUsers, SubjectViewModel } from '../share-panel.d';
import { HttpService } from '../http.service';
import { ButtonStyle } from '../../../aero/aero-button/aero-button.component';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { SharePanelOverlays, ShareStateStore } from '../share-state.store';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';

interface OptionGroup {
  label: string,
  items: SubjectViewModel[]
}

/**
 * Form to allow users to share a unit with accounts or groups.
 *
 * It is configured for one or the other, accounts or for groups.
 */
@Component({
  selector: 'bulb-invite-form',
  templateUrl: './invite-form.component.html',
  styleUrls: ['./invite-form.component.scss'],
  standalone: false,
})
export class InviteFormComponent implements OnInit, OnDestroy {
  /**
   * Used to display the previously invited subjects, and to allow the user to remove them.
   */
  public previouslyInvited: SubjectViewModel[] = [];

  /**
   * Initially a copy of previouslyInvited, which may be modified by the user via this control.
   */
  public updatedInvites: SubjectViewModel[] = [];

  /**
   * Initially a copy of previouslyInvited, which may be modified by the user via this control.
   */
  public invitesForChecklist: SubjectViewModel[] = [];

  /**
   * Whether the form should allow sharing with accounts or with groups.
   */
  public subjectType: 'BULB_USER' | 'BULB_GROUP';

  public parentUnitId: string;

  public compositeUnit: boolean;

  /**
   * Function to retrieve accounts which the logged-in user has recently shared with.
   * Not used for group sharing.
   */
  public fetchRecentlySharedWith: () => Observable<any>;

  /**
   * Function to fetch a page of search results.  Also used to get additional results.
   * Not used for group sharing.
   */
  public fetchSearchPage: (query: string, page: number) => Observable<PageableUsers>;

  /**
   * Style of the cancel button.
   */
  public cancelButtonStyle: ButtonStyle = ButtonStyle.GREY;

  public translationKeys = {
    BULB_USER: {
      title: 'sharePanel.shareWithPeople.title',
      desc: 'sharePanel.shareWithPeople.description',
      label: 'sharePanel.sharingWith',
    },
    BULB_GROUP: {
      title: 'sharePanel.shareWithGroups',
      desc: 'sharePanel.shareToAGroupBySelecting',
      label: 'sharePanel.sharingWith',
    },
  };

  @ViewChild('groupsMultiSelect') groupsMultiSelect: MultiSelect;

  /**
   * Property containing currently selected groups.
   * Not used for sharing with users.
   */
  public selectedGroups: any[] = [];

  public preSelectedGroups: any[] = [];

  /**
   * Configuration groups for the multi-select control used for selecting groups/organizations.
   * Not used for sharing with users.
   */
  public groupedOptions: OptionGroup[] = [
    {
      label: '??Groups',
      items: [],
    },
    {
      label: '??Organizations',
      items: [],
    },
  ];

  private confirmDialogOptions = {
    showHeader: false,
    data: {
      iconKey: 'paper-plane',
      title: 'sharePanel.saveShareSettings',
      description: 'sharePanel.youHaveUnsavedChanges',
      confirmLabel: 'sharePanel.apply',
      dismissLabel: 'sharePanel.dontApply',
    },
  };

  private confirmDialog: DynamicDialogRef;

  constructor(
    private httpSvc: HttpService,
    private inviteFormDialog: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private svgRegistry: SvgIconRegistry,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private modalState: ShareStateStore,
    private aeroDialogService: AeroDialogService,
    @Inject('BulbAuthService') private bulbAuthService,
  ) {
    this.fetchRecentlySharedWith = () => this.httpSvc.fetchRecentlySharedWith(this.bulbAuthService.currentUser.id);
    this.svgRegistry.register([
      faAddressCardIcon,
    ]);
  }

  /**
   * Click handler to treat a click-away event as canceling the dialog.
   * @param event
   */
  @HostListener('window:click', ['$event'])
  handleClick(event: Event): void {
    const { target } = event as any;
    if (target.classList.contains('p-dialog-mask')) {
      this.dismissAndConfirmChanges();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  ngOnInit(): void {
    this.subjectType = this.config.data.subjectType;
    this.fetchSearchPage = this.config.data.fetchSearchPage;
    this.parentUnitId = this.config.data.parentUnitId;
    this.compositeUnit = this.config.data.compositeUnit;
    this.previouslyInvited = Object.freeze(cloneDeep(this.config.data.selectedSubjects));
    this.updatedInvites = cloneDeep(this.config.data.selectedSubjects);
    this.invitesForChecklist = cloneDeep(this.config.data.selectedSubjects);

    if (this.subjectType === 'BULB_GROUP') {
      this.fetchGroups();
      this.groupedOptions[0].label = this.translocoService.translate('sharePanel.select.groups');
      this.groupedOptions[1].label = this.translocoService.translate('sharePanel.select.organizations');
    }
  }

  public onPanelShow() {
    // create new reference, forcing Angular to recognize the change.
    this.groupedOptions = [...this.groupedOptions];
    this.modalState.showPanel(SharePanelOverlays.MULTI_SELECT_PANEL);
  }

  public onPanelHide() {
    this.modalState.hidePanel(SharePanelOverlays.MULTI_SELECT_PANEL);
  }

  private fetchGroups(): void {
    this.httpSvc.fetchGroupsForSharing()
      .pipe(
        take(1),
        map((page) => this.filterExcludedItems(page)),
      )
      .subscribe((results) => {
        // split up the result content into groups and organizations via permissionType
        from(results.content).pipe(
          groupBy((entry: any) => entry.permissionType),
          mergeMap((group$) => group$.pipe(reduce((acc, cur) => [...acc, cur], []))),
        ).subscribe((entry) => {
          if (entry[0]?.permissionType === 'BULB_GROUP') {
            this.groupedOptions[0].items = entry;
            const { items } = this.groupedOptions[0];

            const flattenedItems = [];

            for (const item of items) {
              // Add the original group item
              flattenedItems.push(item);
              if (!this.compositeUnit) {
                flattenedItems.push({
                  // unique temp id
                  id: this.generateObjectId(),
                  parentGroupId: item.id,
                  permissionType: 'BULB_COL',
                  createNew: true,
                  collection: true,
                  name: this.translocoService.translate('sharePanel.createCollection'),
                });

                // If groupCollections exist and have items, add them right after
                if (item.groupCollections?.length) {
                  for (const collection of item.groupCollections) {
                    flattenedItems.push({
                      ...collection,
                      permissionType: 'BULB_COL',
                      parentGroupId: item.id, // optional: keep reference to parent
                      collection: true, // optional: helps you style or handle differently
                    });
                  }
                }
              }
            }

            // Replace the old list with the flattened one
            this.groupedOptions[0].items = flattenedItems;

            let preSelectedChild = null;
            let preSelectedParent = null;

            // Find child from parentUnit
            if (this.parentUnitId) {
              preSelectedChild = flattenedItems.find(
                (item) => item.id === this.parentUnitId,
              );
            }

            // Find parent of child
            if (preSelectedChild) {
              preSelectedParent = flattenedItems.find(
                (item) => item.id === preSelectedChild.parentGroupId,
              );
            }

            // Find checklist matches
            const checklistSelections = flattenedItems.filter(
              (item) => this.invitesForChecklist?.some((inv) => inv.id === item.id),
            );

            // Merge all and remove duplicates
            this.selectedGroups = [
              ...checklistSelections,
              ...(preSelectedChild ? [preSelectedChild] : []),
              ...(preSelectedParent ? [preSelectedParent] : []),
            ].filter(
              (item, index, self) => self.findIndex((i) => i.id === item.id) === index,
            );

            // Disable all selected items
            this.selectedGroups.forEach((item) => {
              item.disabled = true;
            });
            this.preSelectedGroups = [...this.selectedGroups];
          } else {
            this.groupedOptions[1].items = entry;
          }
        });
      });
  }

  /**
   * Remove subjects which have already been invited.
   * This is a business rule to help prevent the user from inviting the same subjects twice.
   */
  private filterExcludedItems(page: PageableUsers): PageableUsers {
    const filteredPage = page;
    // filteredPage.content = page.content.filter(
    //   (item) => !this.previouslyInvited.find(({ id }) => id === item.id),
    // );
    return filteredPage;
  }

  public addSubjectPermissions(subjects: SubjectViewModel[]): void {
    if (subjects[subjects.length - 1].createNew) {
      let collectionValue = '';
      this.aeroDialogService.showDialog({
        title: this.translocoService.translate('sharePanel.createCollection'),
        icon: 'fa fa-exclamation-triangle',
        iconClass: 'aero-highlight',
        showInputField: true,
        primaryDisabled: true,
        styleClass: 'create-collection',
        dialogInput: {
          inputLabel: this.translocoService.translate('sharePanel.collectionName'),
          onInputChange: (value) => {
            collectionValue = value;
            const dialog = document.querySelector('.create-collection');
            const submitBtn = dialog.querySelector('.aero-dialog-primary-button button') as HTMLButtonElement;
            if (collectionValue === '') {
              submitBtn.disabled = true;
              submitBtn.classList.add('p-disabled'); // PrimeNG visual state
            } else {
              submitBtn.disabled = false;
              submitBtn.classList.remove('p-disabled');
            }
          },
        },
        onPrimary: () => {
          let updatedInvites = cloneDeep(this.updatedInvites);
          // Find newly selected item
          const newlySelected = subjects.find(
            (s) => !this.preSelectedGroups.some((p) => p.id === s.id),
          );

          // Save snapshot for next diff
          this.preSelectedGroups = cloneDeep(subjects);

          if (!newlySelected) {
            return;
          }

          // Find its index inside groupedOptions (flattened list)
          const { items } = this.groupedOptions[0];

          const existingIndex = items.findIndex((i) => i.id === newlySelected.id);

          if (existingIndex === -1) {
            return;
          }
          const updatedCollection = this.getNewCollection(items, existingIndex, collectionValue);

          // Replace in groupedOptions so MultiSelect knows it exists
          items.splice(existingIndex + 1, 0, updatedCollection);

          updatedInvites = this.removePreviousCollectionAndGroup(updatedInvites, updatedCollection);

          // Update selectedGroups properly
          this.selectedGroups = this.selectedGroups
            .filter((i) => i.id !== newlySelected.id);

          this.selectedGroups.unshift(updatedCollection);

          this.invitesForChecklist = uniqBy(updatedInvites, 'id');
          this.updatedInvites = updatedInvites;

          // Update snapshot for next diff
          this.preSelectedGroups = cloneDeep(this.selectedGroups);

          setTimeout(() => {
            this.groupsMultiSelect?.hide();
            this.groupedOptions[0].items = this.groupedOptions[0].items.filter(this.isNotSelected.bind(this));
          });
        },
        onSecondary: () => {
          const newlySelected = subjects.find(
            (s) => !this.preSelectedGroups.some((p) => p.id === s.id),
          );

          this.selectedGroups = this.selectedGroups.filter((i) => i.id !== newlySelected.id);
        },
      });
    } else {
      let updatedInvites = cloneDeep(this.updatedInvites);

      // Find newly selected item (diff with previous selection)
      const newlySelected = subjects.find(
        (s) => !this.preSelectedGroups.some((p) => p.id === s.id),
      );

      // If nothing new (user only unchecked something)
      if (!newlySelected) {
        return;
      }

      // ---------------- COLLECTION (CHILD) ----------------
      if (newlySelected.collection === true) {
        updatedInvites = this.removePreviousCollectionAndGroup(updatedInvites, newlySelected);
      } else {
        // ---------------- PARENT ----------------
        updatedInvites.unshift(newlySelected);
        newlySelected.disabled = true;
      }

      this.invitesForChecklist = [...uniqBy(updatedInvites, 'id')];
      this.updatedInvites = [...updatedInvites];

      // Update snapshot for next diff
      this.preSelectedGroups = cloneDeep(this.selectedGroups);

      setTimeout(() => {
        this.groupsMultiSelect?.hide();
        this.groupedOptions[0].items = this.groupedOptions[0].items.filter(this.isNotSelected.bind(this));
      });

    //   setTimeout(() => {
    //     this.groupsMultiSelect?.hide();
    //     this.groupedOptions = [
    //     {
    //       ...this.groupedOptions[0],
    //       items: this.groupedOptions[0].items.filter(this.isNotSelected.bind(this))
    //     },
    //     this.groupedOptions[1],
    //   ];
    // });
    }
  }

  private getNewCollection(items: SubjectViewModel[], existingIndex: number, collectionValue: string) {
    // Create updated version
    return {
      ...items[existingIndex],
      displayName: collectionValue,
      id: this.generateObjectId(),
      createNew: true,
    };
  }

  private removePreviousCollectionAndGroup(updatedInvites: SubjectViewModel[], newlySelected: SubjectViewModel) {
    const existingCollection = this.selectedGroups.find((i) => i.collection === true);

    if (existingCollection && existingCollection.id !== newlySelected.id) {

      const parentId = existingCollection.parentGroupId;

      // Remove parent & child from invites
      updatedInvites = updatedInvites.filter(
        (i) => i.id !== parentId && i.id !== existingCollection.id,
      );

      this.selectedGroups = this.selectedGroups.filter((i) => i.id !== existingCollection.id
        && i.id !== parentId);

      // Re-enable both in groupedOptions
      [parentId, existingCollection.id].forEach((id) => {
        const option = this.groupedOptions[0].items.find((i) => i.id === id);
        if (option) option.disabled = false;
      });
    }

    newlySelected.disabled = true;

    // add new collection
    updatedInvites.unshift(newlySelected);

    // also add its parent
    const parent = this.groupedOptions[0].items.find(
      (i) => i.id === newlySelected.parentGroupId,
    );

    if (parent) {
      updatedInvites.unshift(parent);
      this.selectedGroups.unshift(parent);
      parent.disabled = true;
    }
    return updatedInvites;
  }

  private isNotSelected(subject: SubjectViewModel): boolean {
    const selected = this.invitesForChecklist || [];

    // 1. Direct match → subject already selected → hide it
    if (selected.some((s) => s.id === subject.id)) {
      return true;
    }

    // 2. Find all parents of selected children
    const parentsOfSelectedChildren = new Set(
      selected
        .filter((s) => !!s.parentGroupId)
        .map((s) => s.parentGroupId as string),
    );

    // 3. Find all children of selected parents
    const selectedParents = new Set(
      selected
        .filter((s) => !s.parentGroupId) // these are groups
        .map((s) => s.id),
    );

    // -------------------------------------------------------
    // Case A: A CHILD is selected → remove parent and siblings
    // -------------------------------------------------------

    // A1. Subject IS the parent of a selected child
    if (parentsOfSelectedChildren.has(subject.id)) {
      // return false;
      // subject.disabled = true;
      return true;
    }

    // A2. Subject is a sibling child of a selected child
    if (subject.parentGroupId && parentsOfSelectedChildren.has(subject.parentGroupId)) {
      return true;
    }

    // -------------------------------------------------------
    // Case B: A PARENT GROUP is selected → remove all children
    // -------------------------------------------------------

    // B1. Subject is child of a selected parent
    if (subject.parentGroupId && selectedParents.has(subject.parentGroupId)) {
      return true;
    }

    // B2. Subject IS that selected parent (handled above by direct match also)
    if (selectedParents.has(subject.id)) {
      return true;
    }

    // If none of the rules hide it → keep it
    return true;
  }

  public updateInvited(subjects: SubjectViewModel[]): void {
    this.updatedInvites = subjects;
  }

  public acceptChanges(): void {
    this.inviteFormDialog.close(uniqWith(
      [...this.updatedInvites],
      (a: SubjectViewModel, b: SubjectViewModel) => a.id === b.id && a.inherited === b.inherited,
    ));
    this.messageService.add({
      severity: 'info',
      summary: this.translocoService.translate('sharePanel.messages.pageSent.title'),
    });
  }

  private dismissChanges(): void {
    this.inviteFormDialog.close(this.previouslyInvited);
  }

  public dismissAndConfirmChanges(): void {
    // If the confirmDialog is already open, don't open another one, just dismiss it.
    if (this.confirmDialog) {
      this.confirmDialog.close();
      return;
    }

    // If there are no changes, just dismiss the dialog.
    if (differenceBy(this.previouslyInvited, this.updatedInvites, 'id').length === 0) {
      this.dismissChanges();
      this.confirmDialog?.close();
      return;
    }

    this.showConfirmDialog();
  }

  private generateObjectId(): string {
    return [...Array(24)]
      .map(() => Math.floor(Math.random() * 16).toString(16))
      .join('');
  }

  private showConfirmDialog(): void {
    this.confirmDialog = this.dialogService.open(GenericDialogComponent, this.confirmDialogOptions);

    const closeSubscription = this.confirmDialog.onClose.subscribe((saveChanges: boolean): void => {
      if (saveChanges) {
        this.acceptChanges();
      } else {
        this.dismissChanges();
      }
      closeSubscription.unsubscribe();
      this.confirmDialog = undefined;
    });
  }

  ngOnDestroy() {
    this.confirmDialog?.close();
  }
}
